import { Card, Modal } from "antd";
import React, { useState, useEffect } from "react";
import Footer from "./HomePage/Footer";
import { useMediaQuery } from "react-responsive";
import { theme } from "../../assets/theme";
import heroAboutUs from "../../assets/img/hero-aboutus.png";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import { ExpandOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import useApi from "../../hooks/useApi";
import { API_METHODS, BLANK_VALUE } from "../../constants";
import isoFull from "../../assets/img/certificates/iso-9001-full.jpg";
import isoThumb from "../../assets/img/certificates/iso-9001-thumb.jpg";
import CEFull from "../../assets/img/certificates/ce-compliance-full.jpg";
import CEThumb from "../../assets/img/certificates/ce-compliance-thumb.jpg";
import iso13485Full from "../../assets/img/certificates/iso-13485-full.jpg";
import iso13485Thumb from "../../assets/img/certificates/iso-13485-thumb.jpg";

function About() {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const certificates = [
    {
      id: 1,
      name: "ISO 9001:2015 Certificate",
      description: "Quality Management System",
      thumbnail: isoThumb,
      fullImage: isoFull,
      details:
        "Manufacturing and Distribution of Lab Equipment, Medical Devices and Lab Supplies",
    },
    {
      id: 2,
      name: "CE Compliance Certificate",
      description: "Medical Device Regulation",
      thumbnail: CEThumb,
      fullImage: CEFull,
      details:
        "Class I Council Directive on Medical Device Regulation (Council Regulation 2017/745)",
    },
    {
      id: 3,
      name: "ISO 13485:2016 Certificate",
      description: "Medical Devices Quality Management",
      thumbnail: iso13485Thumb,
      fullImage: iso13485Full,
      details: "Quality Management System for Medical Devices",
    },
  ];

  const [getSeoDataResponse, getSeoDataRequest] = useApi(
    `/website/static-seo?page_identifier=About`,
    null,
    API_METHODS.GET
  );

  const [seoData, setSeoData] = useState(null);

  useEffect(() => {
    getSeoDataRequest();
  }, []);

  useEffect(() => {
    if (getSeoDataResponse.data && !getSeoDataResponse.isLoading) {
      const data =
        getSeoDataResponse.data !== BLANK_VALUE
          ? getSeoDataResponse.data
          : null;
      setSeoData(data);
    }
  }, [getSeoDataResponse]);

  const showCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedCertificate(null);
  };

  const generateOrganizationSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Cenvance Innovations Ltd",
      url: "https://cenvance.com",
      logo: "https://cenvance.com/logo.png",
      description:
        "We provide high-quality solutions that empower research laboratories, hospitals, and diagnostic centers worldwide.",
      sameAs: [
        "https://www.facebook.com/cenvance",
        "https://www.linkedin.com/company/cenvance",
        "https://twitter.com/cenvance",
      ],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "Customer Service",
        email: "info@cenvance.com",
        areaServed: "Worldwide",
      },
    };
  };

  const generateWebPageSchema = () => {
    if (!seoData) return null;

    return {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: seoData.page_identifier || "About Us",
      description: seoData.meta_description,
      url: "https://cenvance.com/about",
      inLanguage: "en-US",
      isPartOf: {
        "@type": "WebSite",
        name: "Cenvance Innovations Ltd",
        url: "https://cenvance.com",
      },
      about: {
        "@type": "Organization",
        name: "Cenvance Innovations Ltd",
      },
      keywords: seoData.meta_keywords,
    };
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>
          {seoData?.meta_title || "About Us - Cenvance Innovations Ltd"}
        </title>
        <meta
          name="description"
          content={
            seoData?.meta_description ||
            "Learn about Cenvance Innovations Ltd - dedicated to revolutionizing laboratory equipment, medical devices, and lab consumables worldwide."
          }
        />
        {seoData?.meta_keywords && (
          <meta name="keywords" content={seoData.meta_keywords} />
        )}
        <meta name="robots" content={seoData?.meta_robots || "index, follow"} />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href={seoData?.canonical_url || "https://cenvance.com/about"}
        />

        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cenvance Innovations Ltd" />
        <meta
          property="og:url"
          content={seoData?.canonical_url || "https://cenvance.com/about"}
        />
        <meta
          property="og:title"
          content={
            seoData?.og_title ||
            seoData?.meta_title ||
            "About Us - Cenvance Innovations Ltd"
          }
        />
        <meta
          property="og:description"
          content={
            seoData?.og_description ||
            seoData?.meta_description ||
            "Learn about Cenvance Innovations Ltd"
          }
        />
        {seoData?.og_image && (
          <meta property="og:image" content={seoData.og_image} />
        )}

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            seoData?.twitter_title ||
            seoData?.meta_title ||
            "About Us - Cenvance Innovations Ltd"
          }
        />
        <meta
          name="twitter:description"
          content={
            seoData?.twitter_description ||
            seoData?.meta_description ||
            "Learn about Cenvance Innovations Ltd"
          }
        />
        {seoData?.twitter_image && (
          <meta name="twitter:image" content={seoData.twitter_image} />
        )}

        {/* Structured Data Schemas */}
        <script type="application/ld+json">
          {JSON.stringify(generateOrganizationSchema())}
        </script>

        {seoData && (
          <script type="application/ld+json">
            {JSON.stringify(generateWebPageSchema())}
          </script>
        )}

        {/* Additional Schema if exists */}
        {seoData?.additional_schema && (
          <script type="application/ld+json">
            {typeof seoData.additional_schema === "string"
              ? seoData.additional_schema
              : JSON.stringify(seoData.additional_schema)}
          </script>
        )}
      </Helmet>

      <div>
        <img
          alt="Hero"
          src={heroAboutUs}
          style={{ height: "auto", width: "100%" }}
        />
      </div>
      <Breadcrumbs
        items={[
          {
            title: "About Us",
          },
        ]}
      />
      <Card
        style={{
          color: theme.colors.darkGray,
          marginBottom: 30,
          marginTop: 30,
          borderRadius: 50,
          width: isTabletOrMobile ? "90%" : "80%",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <h1
          className="about-company-title"
          style={{
            color: theme.colors.black,
            textAlign: "center",
            marginBottom: 30,
          }}
        >
          Cenvance Innovation Limited
        </h1>
        <p className="about-intro-text" style={{ textAlign: "center" }}>
          We are dedicated to revolutionizing the field of laboratory equipment,
          medical devices, and lab consumables. With a strong foundation in
          scientific innovation and precision engineering, we provide
          high-quality solutions that empower research laboratories, hospitals,
          and diagnostic centers worldwide.
        </p>
        <p
          className="about-intro-text"
          style={{
            textAlign: "center",
            marginTop: 20,
          }}
        >
          Our expertise spans a wide range of advanced laboratory instruments
          and medical technologies, ensuring that scientists, healthcare
          professionals, and researchers have access to tools that drive
          discovery and improve patient care. Whether it's microscopes,
          centrifuges, spectrophotometers, or diagnostic analyzers, we deliver
          cutting-edge solutions that meet the highest industry standards.
        </p>
        <h3
          className="about-section-title"
          style={{ color: theme.colors.black, marginTop: 60, marginBottom: 0 }}
        >
          OUR PURPOSE
        </h3>
        <p className="about-text" style={{ textAlign: "justify" }}>
          We are committed to enhancing scientific progress and medical
          advancements by delivering reliable, high-performance equipment. Our
          goal is to support researchers and healthcare professionals with
          state-of-the-art tools that enhance accuracy, efficiency, and safety
          in laboratories and clinical environments.
        </p>
        <h3
          className="about-section-title"
          style={{ color: theme.colors.black, marginTop: 30, marginBottom: 0 }}
        >
          WHY CHOOSE US?
        </h3>
        <ul
          className="about-text"
          style={{
            listStyleType: "disc",
            marginLeft: 20,
          }}
        >
          <li>
            <strong style={{ color: theme.colors.black }}>
              Industry Expertise-
            </strong>{" "}
            Years of experience in laboratory and medical technology.
          </li>
          <li>
            <strong style={{ color: theme.colors.black }}>
              Precision & Quality-
            </strong>{" "}
            Every product meets strict international standards.
          </li>
          <li>
            <strong style={{ color: theme.colors.black }}>
              Innovative Solutions-
            </strong>{" "}
            Designed to improve research and diagnostic efficiency.
          </li>
          <li>
            <strong style={{ color: theme.colors.black }}>Global Reach-</strong>{" "}
            Serving scientists and medical professionals worldwide.
          </li>
          <li>
            <strong style={{ color: theme.colors.black }}>
              Customer-Centric Approach-
            </strong>{" "}
            Providing personalized support and service.
          </li>
        </ul>
        <h3
          className="about-section-title"
          style={{ color: theme.colors.black, marginTop: 30, marginBottom: 0 }}
        >
          INNOVATING FOR A BETTER TOMORROW
        </h3>
        <p className="about-text" style={{ textAlign: "justify" }}>
          At{" "}
          <strong style={{ color: theme.colors.black, fontWeight: 700 }}>
            Cenvance Innovations Ltd.
          </strong>
          , we believe that precision, reliability, and innovation are the
          cornerstones of scientific and medical success. By continuously
          evolving with the latest technologies, we ensure that our clients
          receive the best tools to transform research, diagnostics, and patient
          care.
        </p>
        <p className="about-text" style={{ textAlign: "justify" }}>
          Discover the future of laboratory and medical technology with us.
        </p>

        {/* Certificates Section */}
        <div
          style={{
            marginTop: 40,
            paddingTop: 30,
            borderTop: `1px solid ${theme.colors.lightGray || "#e8e8e8"}`,
            textAlign: "center",
          }}
        >
          <h3
            className="about-section-title"
            style={{
              color: theme.colors.black,
              marginBottom: 20,
              fontSize: "18px",
            }}
          >
            QUALITY CERTIFICATIONS
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isTabletOrMobile ? "1fr" : "repeat(3, 1fr)",
              gap: "30px",
              marginBottom: "20px",
            }}
          >
            {certificates.map((certificate) => (
              <div
                key={certificate.id}
                onClick={() => showCertificate(certificate)}
                style={{
                  position: "relative",
                  cursor: "pointer",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  transition: "all 0.3s ease",
                  backgroundColor: "#fff",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "scale(1.02)";
                  e.target.style.boxShadow = "0 8px 16px rgba(0,0,0,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "scale(1)";
                  e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.1)";
                }}
              >
                <div
                  style={{
                    position: "relative",
                    height: "400px",
                    backgroundImage: `url(${certificate.fullImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      backgroundColor: "rgba(0,0,0,0.7)",
                      borderRadius: "50%",
                      width: "35px",
                      height: "35px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                    }}
                  >
                    <ExpandOutlined style={{ fontSize: "16px" }} />
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background:
                        "linear-gradient(transparent, rgba(0,0,0,0.8))",
                      padding: "20px 15px 15px",
                      color: "white",
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "white",
                      }}
                    >
                      {/* {certificate.name} */}
                    </h4>
                    <p
                      style={{
                        margin: "5px 0 0",
                        fontSize: "12px",
                        opacity: 0.9,
                        color: "white",
                      }}
                    >
                      {/* {certificate.description} */}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certificate Modal */}
        <Modal
          open={isModalVisible}
          onCancel={handleModalClose}
          footer={null}
          width="90%"
          style={{ maxWidth: "800px" }}
          centered
        >
          {selectedCertificate && (
            <div style={{ textAlign: "center" }}>
              <img
                src={selectedCertificate.fullImage}
                alt={selectedCertificate.name}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            </div>
          )}
        </Modal>
      </Card>
      <Footer isTabletOrMobile={isTabletOrMobile} />
    </>
  );
}

export default About;
